import { Plus } from "lucide-react";
import { Button } from "src/components/ui/button";
import NavigationTreeItem from "./NavigationTreeItem";

interface NavigationTreeProps {
	tree: NavigationTreeNode[];
	selectedId: string | null;
	onSelect: (item: NavigationItem) => void;
	onAddTopLevel: () => void;
	onAddChild: (parentId: string) => void;
	onMoveUp: (itemId: string) => void;
	onMoveDown: (itemId: string) => void;
	onDelete: (itemId: string) => void;
}

function findSiblingIndex(
	nodes: NavigationTreeNode[],
	itemId: string
): { siblings: NavigationTreeNode[]; index: number } | null {
	for (let i = 0; i < nodes.length; i++) {
		if (nodes[i].item.id === itemId) return { siblings: nodes, index: i };
		const found = findSiblingIndex(nodes[i].children, itemId);
		if (found) return found;
	}
	return null;
}

const NavigationTree = ({
	tree,
	selectedId,
	onSelect,
	onAddTopLevel,
	onAddChild,
	onMoveUp,
	onMoveDown,
	onDelete
}: NavigationTreeProps) => {
	const canMoveUp = (itemId: string): boolean => {
		const result = findSiblingIndex(tree, itemId);
		return result !== null && result.index > 0;
	};

	const canMoveDown = (itemId: string): boolean => {
		const result = findSiblingIndex(tree, itemId);
		return result !== null && result.index < result.siblings.length - 1;
	};

	return (
		<div className="flex flex-col h-full gap-4">
			<div className="flex items-center justify-between h-9">
				<label className="font-heading text-lg font-medium text-foreground">
					Menu Structure
				</label>
				<Button size="sm" onClick={onAddTopLevel}>
					<Plus className="h-4 w-4 mr-1" />
					Add item
				</Button>
			</div>

			<div className="flex-1 overflow-y-auto rounded-xl border border-border bg-subtle shadow-sm">
				{/* Header row */}
				<div className="flex items-center px-4 py-2.5 border-b border-border text-sm font-medium text-muted-foreground">
					<span className="flex-1">Name</span>
					<span className="w-16 text-center">Layout</span>
					<span className="w-16 text-center">Status</span>
					<span className="w-10" />
				</div>

				{/* Tree rows */}
				<div>
					{tree.map((node) => (
						<NavigationTreeItem
							key={node.item.id}
							node={node}
							depth={0}
							selectedId={selectedId}
							onSelect={onSelect}
							onAddChild={onAddChild}
							onMoveUp={onMoveUp}
							onMoveDown={onMoveDown}
							onDelete={onDelete}
							canMoveUp={canMoveUp}
							canMoveDown={canMoveDown}
						/>
					))}
				</div>
			</div>
		</div>
	);
};

export default NavigationTree;
